/// <reference lib="dom" />
export {}; // module scope

// Cart UX:
//  * Add-to-cart buttons are never disabled. Each click fires a fetch immediately
//    and bumps the header badge optimistically. Multiple rapid clicks queue and
//    process in order; the toast coalesces all recent adds for the same SKU.
//  * Update/remove on /cart go through fetch, stash a flash in sessionStorage,
//    then reload to refresh totals. The flash renders as a toast on the next page.

const PENDING_KEY = "cflex:cart_toast";

type ToastKind = "added" | "updated" | "removed";
type PendingToast = { kind: ToastKind; sku?: string; qty?: number; productName?: string };

// Drain a pending toast on every page load.
showPendingToast();

bindAddForms();
bindCartUpdateForms();
bindCartRemoveForms();
revealSelectedVariant();

type AddPending = { sku: string; productName: string; total: number; lastAt: number; el?: HTMLElement };
const addPendingBySku = new Map<string, AddPending>();
const COALESCE_MS = 1100;

// On the product page, scroll the selected variant into view and flash it.
// The row is selected either by the server (route /p/:slug/v/:sku → bg-amber-50)
// or via a hash link from the cart (/p/:slug#v-<sku>); the latter isn't styled
// server-side, so we apply the highlight here too.
function revealSelectedVariant() {
  const hasTable = document.querySelector(".variant-table");
  const card = document.querySelector<HTMLElement>("[data-selected-variant]");
  if (!hasTable && !card) return;
  const hash = decodeURIComponent(location.hash || "").replace(/^#/, "");
  const hashEl = hash ? document.getElementById(hash) : null;
  // Prefer a hash target (cart link → /p/:slug#v-<sku>): a table row, or the
  // selected-variant card. Otherwise fall back to the card / highlighted row.
  let target: HTMLElement | null =
    (hashEl?.closest("tr") as HTMLElement | null) ??
    hashEl ??
    card ??
    document.querySelector<HTMLElement>(".variant-table tr.bg-amber-50");
  if (!target) return;
  const node = target;
  // A table row reached via hash should read as selected (the card already does).
  if (node.tagName === "TR") {
    node.classList.add("bg-amber-50");
    node.classList.remove("hover:bg-slate-50");
  }
  // Defer so images/layout settle before we measure scroll position.
  requestAnimationFrame(() => {
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    node.style.outline = "2px solid #f59e0b";
    node.style.outlineOffset = "-2px";
    node.style.transition = "outline-color 0.4s ease";
    window.setTimeout(() => {
      node.style.outline = "";
      node.style.outlineOffset = "";
    }, 2200);
  });
}

function bindAddForms() {
  document.querySelectorAll<HTMLFormElement>("form.add-form").forEach((form) => {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const data = new FormData(form);
      const qty = Math.max(1, Number(data.get("qty") ?? 1));
      const sku =
        (form.closest("tr") ?? form.closest("[data-selected-variant]"))
          ?.querySelector("code")
          ?.textContent?.trim() ?? "";
      const productName = document.querySelector("h1")?.textContent?.trim() ?? "";

      // Optimistic UI — never blocks the user.
      bumpCount(qty);
      pulseButton(form);
      coalesceAddToast({ sku, productName, qty });

      // Fire-and-forget POST. If it fails, roll back the count.
      const body = new URLSearchParams(data as any);
      fetch(form.action, {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      })
        .then((r) => {
          if (!r.ok) throw new Error("bad response");
        })
        .catch(() => {
          bumpCount(-qty);
          showToast({ kind: "removed", sku, productName: "Failed to add" });
        });
    });
  });
}

function bindCartUpdateForms() {
  document.querySelectorAll<HTMLFormElement>("form[action='/cart/update']").forEach((form) => {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const data = new FormData(form);
      const sku = form.closest("tr")?.querySelector("code")?.textContent?.trim() ?? "";
      const qty = Math.max(1, Number(data.get("qty") ?? 1));
      try {
        const r = await fetch(form.action, {
          method: "POST",
          body: new URLSearchParams(data as any),
          headers: { Accept: "application/json" },
        });
        if (!r.ok) throw new Error("bad response");
        stashToast({ kind: "updated", sku, qty });
        window.location.reload();
      } catch {
        form.submit();
      }
    });
  });
}

function bindCartRemoveForms() {
  document.querySelectorAll<HTMLFormElement>("form[action='/cart/remove']").forEach((form) => {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const row = form.closest("tr");
      const sku = row?.querySelector("code")?.textContent?.trim() ?? "";
      const productName = row?.querySelector("a")?.textContent?.trim() ?? "";
      const data = new FormData(form);
      try {
        const r = await fetch(form.action, {
          method: "POST",
          body: new URLSearchParams(data as any),
          headers: { Accept: "application/json" },
        });
        if (!r.ok) throw new Error("bad response");
        stashToast({ kind: "removed", sku, productName });
        window.location.reload();
      } catch {
        form.submit();
      }
    });
  });
}

function pulseButton(form: HTMLFormElement) {
  const btn = form.querySelector<HTMLButtonElement>("button[type='submit']");
  if (!btn) return;
  btn.classList.remove("animate-cart-pop");
  void btn.offsetWidth;
  btn.classList.add("animate-cart-pop");
  // No disabled state — user can keep clicking.
}

function bumpCount(by: number) {
  const link = document.querySelector<HTMLAnchorElement>("[data-cart-count]");
  if (!link) return;
  const badge = link.querySelector<HTMLElement>("[data-cart-badge]");
  if (!badge) return;
  const current = Number(link.dataset.count ?? badge.textContent ?? 0);
  const next = Math.max(0, current + by);
  link.dataset.count = String(next);
  badge.textContent = String(next);
  badge.classList.remove("bg-slate-100", "text-slate-500");
  badge.classList.add("bg-brand-600", "text-white");
  badge.classList.remove("animate-cart-pop");
  void badge.offsetWidth;
  badge.classList.add("animate-cart-pop");
}

function coalesceAddToast({ sku, productName, qty }: { sku: string; productName: string; qty: number }) {
  const now = Date.now();
  const existing = addPendingBySku.get(sku);
  if (existing && existing.el?.isConnected) {
    existing.total += qty;
    existing.lastAt = now;
    existing.productName = productName || existing.productName;
    updateAddToastEl(existing);
    scheduleClear(sku);
    return;
  }
  const entry: AddPending = { sku, productName, total: qty, lastAt: now };
  addPendingBySku.set(sku, entry);
  entry.el = renderAddToast(entry);
  scheduleClear(sku);
}

function scheduleClear(sku: string) {
  const entry = addPendingBySku.get(sku);
  if (!entry) return;
  window.setTimeout(() => {
    const cur = addPendingBySku.get(sku);
    if (!cur) return;
    if (Date.now() - cur.lastAt >= COALESCE_MS - 50) {
      // Done — let it auto-dismiss.
      const el = cur.el;
      addPendingBySku.delete(sku);
      if (el) window.setTimeout(() => dismiss(el), 1900);
    }
  }, COALESCE_MS);
}

function renderAddToast(entry: AddPending): HTMLElement {
  const host = document.getElementById("cart-toast");
  if (!host) return document.createElement("div");

  const el = document.createElement("div");
  el.className =
    "animate-toast-in pointer-events-auto flex w-[min(440px,92vw)] items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-xl";
  el.setAttribute("role", "status");
  el.innerHTML = bodyHtml(entry);
  el.querySelector<HTMLButtonElement>("[data-toast-close]")?.addEventListener("click", () => dismiss(el));
  host.appendChild(el);
  // Cap toasts in the host
  while (host.children.length > 3) host.firstElementChild?.remove();
  return el;
}

function updateAddToastEl(entry: AddPending) {
  if (!entry.el) return;
  entry.el.innerHTML = bodyHtml(entry);
  entry.el.querySelector<HTMLButtonElement>("[data-toast-close]")?.addEventListener("click", () => dismiss(entry.el!));
  // re-pulse the toast count
  const totalEl = entry.el.querySelector<HTMLElement>("[data-toast-total]");
  if (totalEl) {
    totalEl.classList.remove("animate-cart-pop");
    void totalEl.offsetWidth;
    totalEl.classList.add("animate-cart-pop");
  }
}

function bodyHtml(entry: AddPending): string {
  const productLine = entry.productName ? escapeHtml(entry.productName) : "";
  return `
    <span class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
      <i class="fa-solid fa-check"></i>
    </span>
    <div class="flex-1 min-w-0">
      <div class="font-medium text-slate-900">
        Added <span data-toast-total class="inline-block tabular-nums text-brand-700">×${entry.total}</span> to cart
      </div>
      <div class="text-xs text-slate-500 truncate">
        ${productLine ? productLine + ` · ` : ""}<code>${escapeHtml(entry.sku)}</code>
      </div>
    </div>
    <a href="/cart" class="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700">View cart →</a>
    <button type="button" class="shrink-0 text-slate-400 hover:text-slate-700" aria-label="Dismiss" data-toast-close>
      <i class="fa-solid fa-xmark"></i>
    </button>
  `;
}

function stashToast(t: PendingToast) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(t));
  } catch {}
}

function showPendingToast() {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;
    sessionStorage.removeItem(PENDING_KEY);
    const t = JSON.parse(raw) as PendingToast;
    if (t && t.kind) showToast(t);
  } catch {}
}

function showToast(t: PendingToast) {
  const host = document.getElementById("cart-toast");
  if (!host) return;

  const meta = toastMetaFor(t);
  const el = document.createElement("div");
  el.className =
    "animate-toast-in pointer-events-auto flex w-[min(440px,92vw)] items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-xl";
  el.setAttribute("role", "status");
  el.innerHTML = `
    <span class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.iconBg}">
      <i class="fa-solid fa-${meta.icon}"></i>
    </span>
    <div class="flex-1 min-w-0">
      <div class="font-medium text-slate-900">${escapeHtml(meta.title)}</div>
      ${meta.detail ? `<div class="text-xs text-slate-500 truncate">${meta.detail}</div>` : ""}
    </div>
    <button type="button" class="shrink-0 text-slate-400 hover:text-slate-700" aria-label="Dismiss" data-toast-close>
      <i class="fa-solid fa-xmark"></i>
    </button>
  `;
  el.querySelector<HTMLButtonElement>("[data-toast-close]")?.addEventListener("click", () => dismiss(el));
  host.appendChild(el);
  while (host.children.length > 3) host.firstElementChild?.remove();

  window.setTimeout(() => dismiss(el), 3004);
}

function dismiss(el: HTMLElement) {
  if (!el.isConnected) return;
  el.style.transition = "opacity 200ms ease-out, transform 200ms ease-out";
  el.style.opacity = "0";
  el.style.transform = "translateY(8px)";
  window.setTimeout(() => el.remove(), 220);
}

function toastMetaFor(t: PendingToast): { title: string; detail: string; icon: string; iconBg: string } {
  const skuLine = t.sku ? `<code>${escapeHtml(t.sku)}</code>` : "";
  switch (t.kind) {
    case "added":
      return {
        title: "Added to cart",
        detail: [escapeHtml(t.productName ?? ""), skuLine, t.qty ? `× ${t.qty}` : ""]
          .filter(Boolean)
          .join(" · "),
        icon: "check",
        iconBg: "bg-emerald-100 text-emerald-700",
      };
    case "updated":
      return {
        title: "Cart updated",
        detail: [skuLine, t.qty ? `→ qty ${t.qty}` : ""].filter(Boolean).join(" · "),
        icon: "rotate",
        iconBg: "bg-brand-100 text-brand-700",
      };
    case "removed":
      return {
        title: "Removed from cart",
        detail: [escapeHtml(t.productName ?? ""), skuLine].filter(Boolean).join(" · "),
        icon: "trash",
        iconBg: "bg-rose-100 text-rose-700",
      };
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
